import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { shiftDateOnly, todayInputValue } from '../../lib/taskDates';
import { dayLabel } from '../../lib/dailyChecklist';
import { useDayBoundary } from '../../lib/useDayBoundary';
import { useDailyChecklist } from './useDailyChecklist';
import { DailyChecklistList } from './DailyChecklistList';
import { Alert, Card, EmptyState, FOCUS_RING, SkeletonRows } from '../ui';
import { TaskComposer } from '../tasks/TaskComposer';
import { ChecklistIcon, ChevronLeftIcon, ChevronRightIcon } from '../icons';

/** 28px square, so the two arrows match the hit area CHECKBOX_BOX grows for
 *  itself and the stepper is no taller than the line of text in it. */
const STEP_BUTTON =
  `flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-stone-500 transition ` +
  `hover:bg-stone-100 hover:text-stone-900 disabled:pointer-events-none disabled:opacity-25 ${FOCUS_RING}`;

/**
 * What would make today a win.
 *
 * The third question on this page, under the two the task cards ask. Not a task
 * list: most of what goes here is not work anybody would file, and none of it
 * survives the day. An unticked line does not follow you into tomorrow, because
 * tomorrow gets decided on its own terms -- what is left at midnight is the
 * record of how that day went, which is what the arrows and the history page are
 * for.
 *
 * Unlike DayTasksCard and FocusCard this owns its own hook rather than being
 * handed one from the page. Those two are lifted because the task panel opens
 * over the dashboard and its edits have to reach the rows behind it; nothing
 * writes into this list from outside, so lifting it would be ceremony that made
 * the page read as if a fourth thing needed coordinating. A panel over these
 * rows is what would change it.
 */
export function DailyChecklistCard({
  onWins,
}: {
  /**
   * Today's score, whenever it changes -- and `null` whenever this card is not
   * showing today.
   *
   * The stats card above reports the same number, sitting directly over the
   * list it counts, so the two must never disagree. Handing it over is what
   * makes that structural: it reads what this card already knows rather than
   * asking the server to re-derive it, which was a round trip behind every tick
   * and visibly lagged the line the reader had just crossed out.
   */
  onWins?: (wins: { done: number; total: number } | null) => void;
} = {}) {
  const [today, setToday] = useState(todayInputValue);
  const [day, setDay] = useState(today);
  const dayRef = useRef(day);
  dayRef.current = day;

  useDayBoundary(
    useCallback((now: string, previous: string) => {
      setToday(now);
      // Only the view that *was* today follows the clock. Somebody who walked
      // back with the arrows is reading something, and yanking them to a blank
      // new day at midnight takes the page away mid-sentence. All that changes
      // for them is the label and which end the stepper stops at.
      if (dayRef.current === previous) setDay(now);
    }, []),
  );

  const canEdit = day === today;
  const list = useDailyChecklist(day);

  const { items, doneCount, loading } = list;

  // Reported from the rendered score rather than from each mutation, so the
  // optimistic tick and the number above it move in the same paint. `null` on a
  // past day and while a day is in flight: the stat is always today's, and a
  // day being loaded has no score to give yet.
  const winsRef = useRef(onWins);
  winsRef.current = onWins;
  useEffect(() => {
    winsRef.current?.(canEdit && !loading ? { done: doneCount, total: items.length } : null);
  }, [canEdit, loading, doneCount, items.length]);

  /**
   * The score is blank while a day is in flight.
   *
   * `items` still holds the day you stepped away from until the next reply
   * lands, so without this the header spends a moment reading "Yesterday"
   * over today's 4/6 -- a wrong number under the right label, which is worse
   * than no number at all. The body is already showing a skeleton; this is
   * the header agreeing with it.
   */
  const score = list.loading || list.loadError ? null : { done: doneCount, total: items.length };

  const step = (days: -1 | 1) => setDay((current) => shiftDateOnly(current, days));

  return (
    <Card>
      {/*
        No `border-b` on this header: the progress rail at the bottom of it is
        the separator. That is how a progress bar fits in a one-third column
        without costing a row -- its track is the border colour, so an untouched
        day looks exactly like every other card on the page and a finished one is
        a clean emerald line.
      */}
      <div>
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <h2 className="flex min-w-0 items-baseline gap-2 text-lg font-semibold tracking-tight">
            <span className="truncate">Today&rsquo;s Win</span>
            {/* `{done}/{total}` rather than a CountBadge: a badge says how many,
                this says how far, and the task panel's checklist already renders
                the pair exactly this way. `tabular-nums` so ticking one off does
                not shuffle the title sideways. */}
            {score && score.total > 0 ? (
              <span className="shrink-0 text-sm font-normal tabular-nums text-stone-500">
                {score.done}/{score.total}
              </span>
            ) : null}
          </h2>

          {/* The Pinned card's "Browse all", same corner and same skin -- the two
              share this column and should not disagree about where the way out
              of a card lives. */}
          <Link
            to={canEdit ? '/wins' : `/wins?day=${day}`}
            className="shrink-0 rounded text-sm font-medium text-honey-700 hover:text-honey-800 hover:underline"
          >
            History
          </Link>
        </div>

        {/* A stepper rather than two buttons in a corner: arrows at both ends of
            a centred label is the one date control nobody has to be taught, and
            it survives the label growing from "Today" to "12 Sep". */}
        <div className="flex items-center gap-1 px-2 pb-2">
          <button type="button" aria-label="Previous day" onClick={() => step(-1)} className={STEP_BUTTON}>
            <ChevronLeftIcon />
          </button>

          <span
            aria-live="polite"
            className="min-w-0 flex-1 truncate text-center text-dense font-medium text-stone-600"
          >
            {dayLabel(day, today)}
          </span>

          {/* Disabled on today. Nothing can be written to a future day, and
              since nothing carries over it would be blank forever -- an enabled
              arrow onto a permanently empty unwritable page reads as a bug.
              Disabled rather than hidden, so the label does not slide. */}
          <button
            type="button"
            aria-label="Next day"
            disabled={canEdit}
            onClick={() => step(1)}
            className={STEP_BUTTON}
          >
            <ChevronRightIcon />
          </button>
        </div>

        <div aria-hidden="true" className="h-0.5 w-full bg-stone-200">
          <div
            className="h-full bg-emerald-500 transition-[width] duration-300 ease-out"
            style={{ width: score && score.total > 0 ? `${(score.done / score.total) * 100}%` : '0%' }}
          />
        </div>
      </div>

      {list.actionError ? (
        <div className="border-b border-stone-100 p-3">
          <Alert onDismiss={list.dismissActionError}>{list.actionError}</Alert>
        </div>
      ) : null}

      {list.loadError ? (
        <div className="p-4">
          <Alert>{list.loadError}</Alert>
        </div>
      ) : list.loading ? (
        <div className="px-4 py-2">
          <SkeletonRows count={3} />
        </div>
      ) : items.length === 0 ? (
        <div className="p-4">
          {canEdit ? (
            // No `action`: the composer underneath is the action.
            <EmptyState compact icon={<ChecklistIcon className="h-5 w-5" />} title="Nothing listed yet">
              What would make today a win?
            </EmptyState>
          ) : (
            // Deliberately not today's invitation. Offering "what would make
            // today a win" on a day you cannot write to is the worst cell in
            // this matrix.
            <p className="text-dense text-stone-500">Nothing was listed that day.</p>
          )}
        </div>
      ) : (
        <DailyChecklistList
          items={items}
          canEdit={canEdit}
          onToggle={(item) => void list.toggle(item)}
          onRename={(item, draft) => void list.rename(item, draft)}
          onRemove={(item) => void list.remove(item)}
        />
      )}

      {canEdit && !list.loadError ? (
        <div className="border-t border-stone-100 p-2">
          <TaskComposer placeholder="Add something that makes today a win" onSubmit={list.add} />
        </div>
      ) : null}
    </Card>
  );
}
